import { Injectable, OnInit } from '@angular/core';
import { Cookie } from 'ng2-cookies';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse, HttpRequest } from '@angular/common/http';
import { AuthenticationService } from '../../auth';
import { Globals } from '../../globals';
import { Observable } from 'rxjs';

@Injectable()
export class PersonViewService implements OnInit {

    constructor(private http: HttpClient, private authService: AuthenticationService, private globals: Globals) { }

    ngOnInit() : void {
        this.authService.isTokenValid();
    }

    /**
     * Get person by id
     * @param personId unique id of person
     */
    getPersonById(personId) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/' + personId;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'});
    }

    /**
     * Makes a soft delete on a person
     * @param personId person unique id
     */
    softDeletePerson(personId) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/' + personId;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.delete(url, {headers: requestHeaders, responseType: 'json'});
    }

    /**
     * Restores a soft delete by updating a person
     * @param personId person unique id
     */
    restoreSoftDeletedPerson(personId) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/restore/' + personId;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.put(url, null, {headers: requestHeaders, responseType: 'json'});
    }

    /**
     * Save new notes
     * @param personId person unique id
     * @param notes notes object
     */
    saveNotes(personId, notes) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/' + personId + '/notes';
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        const body = new HttpParams()
            .set('notesBody', JSON.stringify(notes));

        return this.http.post(url, body, {headers: requestHeaders, responseType: 'json'});
    }

    /**
     * Delete notes
     * @param personId person unique id
     * @param noteId notes unique id
     */
    deleteNotes(personId, noteId) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/person/' + personId + '/notes/' + noteId;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.delete(url, {headers: requestHeaders, responseType: 'json'});
    }

    /**
     * Get all employers
     */
    getAllEmployers() : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/employer/';

        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
     * Get all employers based on personId
     * This is done in order to set a selected department to the entity
     * @param personId person unique id to filter employers on
     */
    getAllEmployersByPersonId(personId) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/employer/person/' + personId;

        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
     * Get all projects
     */
    getAllProjects() {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/default-project/';

        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
    * Get project by id
    */
    getProjectById(projectId) {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/default-project/' + projectId;

        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
    * Get all RKs based on personId
    * @param personId person unique id to filter RKs on
    */
    getRkByPersonId(personId) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/default-registercontrol/person/' + personId;

        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
     * Upload a file to be parsed with OCR
     * @file fil
     */
    upload(file : FormData) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/import/';
        let requestHeaders = new HttpHeaders()
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        const req = new HttpRequest('POST', url, file, {
          headers: requestHeaders,
          reportProgress: true,
          responseType: 'text'
        });

        return this.http.request(req);
    }

    saveAttachment(attachment, type, entittyId) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/import/attachment/'+type+'/'+entittyId;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        const body = new HttpParams()
        .set('attachmentBody', JSON.stringify(attachment));

        return this.http.post(url, body, {headers: requestHeaders, responseType: 'json'});
    }

    showAttachedFile(id) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/import/showAttachment/'+id;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'blob'})
    }
    
    getDepartmentById(departmentId) {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/govdepartment/' + departmentId;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'json'})
    }

    /**
     * Delete an attachment belonging to a person
     * @param attachmentId uuid of the attachment
     * @param personId uuid of the person
     */
    deleteAttachment(attachmentId, personId) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/import/personattachment/' + attachmentId + '/' + personId;

        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.delete(url, {headers: requestHeaders, responseType: 'json'});
    }

    getPdf(id) : Observable<any> {
        this.authService.isTokenValid();
        const url = this.authService.getResourceURL() + '/api/v1/import/showAttachment/'+id;
        let requestHeaders = new HttpHeaders()
            .set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
            .set('Authorization', 'Bearer ' + this.authService.getAccessToken());

        return this.http.get(url, {headers: requestHeaders, responseType: 'blob'})
    }
}
